<script lang="ts">
  import DateTimeInput from "@shared/svelte/Components/DateTimeInput.svelte";
  import Flash from "@shared/services/flashv2.js";
  import Icon from "@shared/svelte/Components/Icon.svelte";
  import Modal from "@shared/svelte/Components/Modal.svelte";
  import SimpleAutocompleteDropdown from "@shared/svelte/Components/Autocomplete/SimpleAutocompleteDropdown.svelte";

  import { HttpPromise } from "@shared/svelte/Modules/ApiService";

  import type { DistributorVacation } from "@shared/types/Distributor";
  import type { SalesRep } from "@shared/types/SalesRep";

  export let showVacationModal: boolean;
  export let showOrEditModal: string;
  export let vacations: DistributorVacation[];
  export let vacationToEdit: DistributorVacation;

  let assignedSalesRep: string;
  let orderFromSalesRep: string;
  let salesRep: SalesRep;
  let usersParam: boolean;
  let vacationParams: DistributorVacation = {};

  const salesRepApiHandler = async (input: string) => {
    try {
      let res = await HttpPromise({
        method: "GET",
        url: `/api/distributor/sales_reps`,
        data: {
          search: input,
          require_users: usersParam,
          retailers: true,
        },
      });

      let salesRepsWithName = res.map(rep => {
        return { ...rep, name: rep.display_name };
      });
      return { data: salesRepsWithName };
    } catch {
      return { data: [] };
    }
  };

  const createVacation = async () => {
    try {
      let res = await HttpPromise({
        method: "POST",
        url: "/api/distributor/sales_rep_vacations/",
        data: {
          sales_rep_vacation: { ...vacationToEdit, ...vacationParams },
        },
      });

      vacations.push(res);
      Flash.now({
        type: "success",
        message: `Vacation ${showOrEditModal == "new" ? "created" : "updated"}.`,
      });
      showVacationModal = false;
    } catch (res) {
      Flash.now({
        type: "error",
        message: res.join(", ") || "Error updating vacation",
      });
    }
    vacationParams = {};
  };

  const deleteVacation = async () => {
    try {
      let res = await HttpPromise({
        method: "DELETE",
        url: `/api/distributor/sales_rep_vacations/${vacationToEdit.id}`,
      });
      // remove vacations to update card removal
      vacations = vacations.filter(x => x.id != vacationToEdit.id);
      Flash.now({
        type: "success",
        message: "Vacation Deleted",
      });
      showVacationModal = false;
    } catch {
      Flash.now({
        type: "error",
        message: "Error deleting vacation",
      });
    }
    vacationParams = {};
  };

  const updateVacation = async () => {
    if (vacationToEdit.no_end_time) {
      vacationParams.end_time = null;
    }

    try {
      let res = await HttpPromise({
        method: "PUT",
        url: `/api/distributor/sales_rep_vacations/${vacationToEdit.id}`,
        data: {
          sales_rep_vacation: { ...vacationToEdit, ...vacationParams },
        },
      });
      let vacationToUpdate = vacations.findIndex(x => x.id == vacationToEdit.id);
      vacations[vacationToUpdate] = { ...vacationToEdit, ...vacationParams };
      Flash.now({
        type: "success",
        message: `Vacation ${showOrEditModal == "new" ? "created" : "updated"}.`,
      });
      showVacationModal = false;
    } catch (res) {
      Flash.now({
        type: "error",
        message: res.join(", ") || "Error updating vacation",
      });
    }
    vacationParams = {};
  };

  const requireUsers = (node, boolean) => {
    usersParam = boolean;
  };

  const close = () => {
    showVacationModal = false;
  };
</script>

<Modal bind:show={showVacationModal} fizzModal={true} size="large" overflow="visible">
  <div data-testid="vacation-modal">
    <div class="layout-header-footer">
      <header class="fizz-modal-header fizz-inset-horizontal-16">
        <div class="fizz-heading-5">{showOrEditModal == "new" ? "Add New" : "Edit"} Vacation</div>
        <div class="fizz-modal-close" on:click={close}>
          <Icon
            type="X"
            extraClassNames="fizz-text-brand cursor-pointer"
            dataTestId="close-modal-x"
            height="15"
            width="15"
          />
        </div>
      </header>
    </div>
    <div></div>
    <div class="fizz-stack-16 fizz-inset-horizontal-16">
      <label class="fizz-label fizz-stack-4" use:requireUsers={true}>Assign Orders To</label>
      <SimpleAutocompleteDropdown
        autofocus={true}
        inputId="assigned-sales-rep-autocomplete"
        apiHandler={salesRepApiHandler}
        text={vacationToEdit.assigned_sales_rep
          ? vacationToEdit.assigned_sales_rep.name
          : assignedSalesRep || ""}
        placeholder="Search Sales Reps by Name or Email..."
        showCloseIcon={false}
        addFromAutocompleteOnly={false}
        value={assignedSalesRep}
        on:select={event => {
          if (event.detail.ids) {
            vacationParams.assigned_sales_rep_id = event.detail.ids[0];
            vacationParams.assigned_sales_rep = event.detail;
          } else {
            vacationParams.assigned_email = event.detail;
            vacationToEdit = { id: vacationToEdit.id };
            assignedSalesRep = event.detail;
          }
        }}
        on:clear={() => (salesRep = null)}
      />
    </div>
    <div class="fizz-stack-16 fizz-inset-horizontal-16">
      <label class="fizz-label fizz-stack-4" for="orderFromSalesRep" use:requireUsers={false}
        >Assign Orders From (must already have an account)</label
      >
      <SimpleAutocompleteDropdown
        inputId="sales-rep-autocomplete"
        apiHandler={salesRepApiHandler}
        text={vacationToEdit.sales_rep ? vacationToEdit.sales_rep.name : ""}
        placeholder="Leave blank to assign your own orders"
        showCloseIcon={false}
        addFromAutocompleteOnly={false}
        bind:value={orderFromSalesRep}
        on:select={event => {
          vacationParams.user_id = event.detail.user_ids[0];
          vacationParams.sales_rep = event.detail;
        }}
        on:clear={() => (orderFromSalesRep = null)}
      />
    </div>
    <div class="fizz-stack-16 fizz-inset-horizontal-16 fizz-flex-row-responsive">
      <div class="fizz-half-width">
        <div class="fizz-label fizz-stack-4">Start Date</div>
        <div class="fizz-flex-row">
          <DateTimeInput
            bind:timestamp={vacationToEdit.start_time}
            minDateTimestamp={new Date().toDateString()}
            position="top: -7rem"
            showTimePicker={true}
            testIdPrefix="start"
            on:select={event => {
              vacationParams.start_time = event.detail;
            }}
          />
        </div>
      </div>
      {#if !vacationToEdit.no_end_time}
        <div class="fizz-half-width">
          <div class="fizz-label fizz-stack-4">End Date</div>
          <div class="fizz-flex-row">
            <DateTimeInput
              bind:timestamp={vacationToEdit.end_time}
              minDateTimestamp={vacationToEdit.start_time}
              position="top: -12rem"
              showTimePicker={true}
              testIdPrefix="end"
              on:select={event => {
                vacationParams.end_time = event.detail;
              }}
            />
          </div>
        </div>
      {/if}
    </div>
    <div class="fizz-stack-16 fizz-inset-horizontal-16">
      <!-- if no end time -->
      <div>
        <label class="fizz-flex-row-8 fizz-stack-12 fizz-text-strong fizz-inline-block">
          <input
            type="checkbox"
            class="fizz-checkbox"
            data-testid="no-end-date"
            bind:checked={vacationToEdit.no_end_time}
          />
          <span class="fizz-text-strong">Indefinite vacation (no end date)</span>
        </label>
      </div>
      <div>
        <label class="fizz-flex-row-8 fizz-stack-12 fizz-inline-block">
          <input
            type="checkbox"
            class="fizz-checkbox"
            data-testid="keep-notifying"
            bind:checked={vacationToEdit.keep_notifying}
          />
          <span class="fizz-text-strong">Receive notifications during vacations</span>
        </label>
      </div>
    </div>
    <div class="fizz-flex-row fizz-stack-16 fizz-inset-horizontal-16">
      {#if showOrEditModal != "new"}
        <div class="fizz-text-left">
          <button
            on:click={deleteVacation}
            class="fizz-inline-block half-width fizz-button-medium fizz-button-danger"
          >
            Delete Vacation
          </button>
        </div>
      {/if}
      <div class="fizz-text-right fizz-full-width">
        <button
          on:click={showOrEditModal == "new" ? createVacation : updateVacation}
          disabled={false}
          class="fizz-button-primary fizz-button-medium fizz-inline-block quarter-width"
          type="submit"
        >
          Save
        </button>
      </div>
    </div>
  </div>
</Modal>
